/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import { useHistory } from 'react-router-dom';

import { request } from '@mairistem/tools';
import { ModalEntites } from '@mairistem/entites';
import { Dimmer, Loader } from '@mairistem/composants';

import type { ApiListResponse } from '@mairistem/administres-utils';

import { useFetchHubeeStatus, useMaintenanceInsee } from '@/hooks';
import {
  useEntiteStore,
  useHubeeStore,
  useProduitDomaineStore,
} from '@/zustand';
import type { Entite } from '@/types';

interface EntiteZustandProviderProps {
  children: React.ReactNode;
}

const EntiteZustandProvider = (props: EntiteZustandProviderProps) => {
  const { children } = props;

  const selectedEntite: Entite = useEntiteStore(
    (state) => state.selectedEntite,
  );
  const selectEntite = useEntiteStore((state) => state.selectEntite);
  const setBlocked = useEntiteStore((state) => state.setBlocked);
  const refresh = useEntiteStore((state) => state.refresh);
  const setRefresh = useEntiteStore((state) => state.setRefresh);
  const produitDomaine = useProduitDomaineStore(
    (state) => state.produitDomaine,
  );
  const setHubeeStatus = useHubeeStore((state) => state.setHubeeStatus);
  const { fetchHubeeStatus } = useFetchHubeeStatus();
  const lancerMaintenanceInsee = useMaintenanceInsee();

  const history = useHistory();

  const [openModal, setOpenModal] = React.useState(false);

  const handleSelect = React.useCallback(
    (entite) => {
      setRefresh(false);
      selectEntite(entite);
    },
    [selectEntite, setRefresh],
  );

  const handleClose = React.useCallback(() => {
    setRefresh(false);
    setOpenModal(false);
    history.goBack();
  }, [history, setRefresh]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: no need
  React.useEffect(() => {
    if (_.isNil(selectedEntite) && !_.isNil(produitDomaine)) {
      request
        .get('/api/administres/etat-civil/entites')
        .then(({ data }: ApiListResponse<Entite>) => {
          if (data.length === 1) {
            selectEntite(data[0]);
          } else {
            setOpenModal(true);
          }
        });
    }
  }, [produitDomaine]);

  React.useEffect(() => {
    if (_.isNil(selectedEntite)) {
      setBlocked(true);
      setHubeeStatus(null);
    } else {
      setOpenModal(false);
      setBlocked(false);
      // Comme dans RCO : statut HubEE chargé dès la sélection de l'entité
      fetchHubeeStatus();
    }
  }, [selectedEntite, setBlocked, setHubeeStatus, fetchHubeeStatus]);

  React.useEffect(() => {
    if (refresh) {
      setOpenModal(true);
    }
  }, [refresh]);

  // Ajoute le header X-Entite aux requêtes
  React.useEffect(() => {
    const xEntite = selectedEntite?.identifiant;

    const interceptor = request.interceptors.request.use((config) => {
      // eslint-disable-next-line no-param-reassign
      config.headers['X-Entite'] = xEntite;

      return config;
    });

    return () => {
      request.interceptors.request.eject(interceptor);
    };
  }, [selectedEntite]);

  // Relance et purge du transfert INSEE, comme WinDev au démarrage. Placé après
  // l'intercepteur : les requêtes doivent partir avec le X-Entite de l'entité.
  React.useEffect(() => {
    if (!_.isNil(selectedEntite?.identifiant)) {
      lancerMaintenanceInsee(selectedEntite.identifiant);
    }
  }, [selectedEntite?.identifiant, lancerMaintenanceInsee]);

  return (
    <>
      {children}
      {!_.isNil(produitDomaine) && (
        <ModalEntites
          open={openModal}
          entiteSelected={selectedEntite}
          onSelect={handleSelect}
          onSave={() => null}
          onOpen={() => setOpenModal(true)}
          service="/api/administres/etat-civil/entites"
          // onClose={handleClose}
          {...(refresh && { onClose: handleClose })}
        />
      )}
      {_.isNil(selectedEntite) && (
        <Dimmer active inverted>
          <Loader size="large" />
        </Dimmer>
      )}
    </>
  );
};

export default EntiteZustandProvider;
